/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-runloop */
import { tracked } from '@glimmer/tracking';
import PaperSlider from 'ember-paper/components/paper-slider/component';
import { action, computed } from '@ember/object';
import { bind } from '@ember/runloop';
import { tagName } from '@ember-decorators/component';

/* global Hammer */

@tagName('')
class PaperArraySlider extends PaperSlider {
  // Array values are discrete.  -1 is used for "not selected"; array index is used otherwise (0 to length-1).
  discrete = true;
  min = -1;
  // tipLabelPath is the property path of the label to display in the "tip" while sliding.
  @tracked tipLabelPath = 'label';

  // value is the index of the selected item (-1 if nothing selected).
  @computed('selected', 'options.[]')
  get value() {
    if (this.selected) {
      return this.options.indexOf(this.selected);
    }
    return -1;
  }

  @computed('selected', 'tipLabelPath')
  get label() {
    if (this.selected) {
      return this.selected.get(this.tipLabelPath);
    } else {
      return '';
    }
  }

  // setValueFromEvent is overridden to send the selected object, not the
  // numerical value (index).
  setValueFromEvent(value) {
    let exactVal = this.percentToValue(this.positionToPercent(value));
    let closestVal = this.minMaxValidator(this.stepValidator(exactVal));
    let selected = this.options[closestVal];

    if (this.onChange) {
      this.onChange(selected);
    }
  }

  // handleKeyDown is overridden to send the selected object, not the
  // numerical value (index).
  @action
  handleKeyDown(event) {
    if (this.disabled) {
      return;
    }

    let changeAmount, newValue;

    if (['ArrowLeft', 'Left'].includes(event.key)) {
      changeAmount = parseFloat(this.step) * -1;
    } else if (['ArrowRight', 'Right'].includes(event.key)) {
      changeAmount = parseFloat(this.step);
    }

    if (changeAmount) {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        changeAmount *= 4;
      }

      newValue = this.value + changeAmount;
      let closestVal = this.minMaxValidator(newValue);
      let selected = this.options[closestVal];

      if (this.onChange) {
        this.onChange(selected);
      }

      event.preventDefault();
      event.stopPropagation();
    }
  }

  // _setupHammer is overridden to avoid a naming conflict between existing
  // component args and the onDragStart/onDragEnd functions.
  _setupHammer() {
    // Enable dragging the slider
    let containerManager = new Hammer.Manager(this.element);
    let pan = new Hammer.Pan({
      direction: Hammer.DIRECTION_HORIZONTAL,
      threshold: 10,
    });
    containerManager.add(pan);
    let tap = new Hammer.Tap();
    containerManager.add(tap);

    containerManager
      .on('panstart', bind(this, this._onDragStart))
      .on('panmove', bind(this, this.onDrag))
      .on('panend', bind(this, this._onDragEnd))
      .on('tap', bind(this, this.onTap));

    this._hammer = containerManager;
  }

  // Add onDragStart/onDragEnd so that the consumer can detect when a drag finishes.
  // This makes it possible to wait for the user to release the mouse / touch before processing the change.
  _onDragStart() {
    if (this.onDragStart) {
      this.onDragStart();
    }
    super.onDragStart(...arguments);
  }

  _onDragEnd() {
    if (this.onDragEnd) {
      this.onDragEnd();
    }
    super.onDragEnd(...arguments);
  }
}

export default PaperArraySlider;
